import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

/* Colori per zona: tinte distinguibili anche a colpo d'occhio */
const COLORI_ZONA = {
  'Appia':                  '#dc2626',
  'Aurelio':                '#ea580c',
  'Casalotti-Palmarola':    '#d97706',
  'Casilina-Tuscolana':     '#ca8a04',
  'Cassia-Flaminia':        '#65a30d',
  'Centro Storico':         '#16a34a',
  'EUR-Magliana':           '#059669',
  'Fuori Roma':             '#0d9488',
  'Marconi':                '#0891b2',
  'Nomentana-Salaria':      '#0284c7',
  'Ostiense':               '#2563eb',
  'Portuense-Bravetta':     '#4f46e5',
  'Prati-Vaticano-Parioli': '#7c3aed',
  'Tiburtina-Prenestina':   '#c026d3',
  'Torrevecchia-Boccea':    '#db2777',
};
const COLORE_DEFAULT = '#6b7280';

const coloreZona = z => COLORI_ZONA[z] || COLORE_DEFAULT;

/* Carica Leaflet dal CDN una sola volta */
function useLeaflet() {
  const [pronto, setPronto] = useState(!!window.L);
  useEffect(() => {
    if (window.L) { setPronto(true); return; }
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css';
    document.head.appendChild(css);
    const js = document.createElement('script');
    js.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.js';
    js.onload = () => setPronto(true);
    document.body.appendChild(js);
  }, []);
  return pronto;
}

export default function MappaClientiPage() {
  const leafletPronto = useLeaflet();
  const navigate = useNavigate();

  const [clienti, setClienti]   = useState([]);
  const [senzaCoord, setSenza]  = useState(0);
  const [loading, setLoading]   = useState(true);
  const [zoneAttive, setZone]   = useState(null);   // null = tutte
  const [cerca, setCerca]       = useState('');
  const [includiSospesi, setSospesi] = useState(false);

  const mappaRef  = useRef(null);
  const divRef    = useRef(null);
  const markerRef = useRef([]);

  /* ── Dati ── */
  const carica = useCallback(() => {
    setLoading(true);
    api.get(`/mappa/clienti?includiSospesi=${includiSospesi}`)
      .then(r => {
        setClienti(r.data?.data || []);
        setSenza(r.data?.senzaCoordinate || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [includiSospesi]);

  useEffect(() => { carica(); }, [carica]);

  /* ── Creazione mappa ── */
  useEffect(() => {
    if (!leafletPronto || mappaRef.current || !divRef.current) return;
    const L = window.L;
    const m = L.map(divRef.current).setView([41.9028, 12.4964], 11);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(m);
    mappaRef.current = m;
  }, [leafletPronto]);

  /* ── Elenco zone presenti ── */
  const zone = [...new Set(clienti.map(c => c.zona).filter(Boolean))].sort();

  const visibili = clienti.filter(c => {
    if (zoneAttive && !zoneAttive.has(c.zona || '')) return false;
    if (cerca) {
      const q = cerca.toLowerCase();
      const testo = `${c.nome} ${c.indirizzo || ''} ${c.citta || ''}`.toLowerCase();
      if (!testo.includes(q)) return false;
    }
    return true;
  });

  /* ── Disegno marker ── */
  useEffect(() => {
    const m = mappaRef.current;
    if (!m || !window.L) return;
    const L = window.L;

    markerRef.current.forEach(mk => m.removeLayer(mk));
    markerRef.current = [];

    visibili.forEach(c => {
      const col = coloreZona(c.zona);
      const icona = L.divIcon({
        className: '',
        html: `<div style="width:14px;height:14px;border-radius:50%;
                 background:${col};border:2px solid #fff;
                 box-shadow:0 0 3px rgba(0,0,0,.5);
                 ${c.sospeso ? 'opacity:.45;' : ''}"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });

      const mk = L.marker([c.latitudine, c.longitudine], { icon: icona }).addTo(m);

      const impianti = [
        c.n_estintori ? `${c.n_estintori} estintori` : null,
        c.n_idranti   ? `${c.n_idranti} idranti`     : null,
        c.n_porte     ? `${c.n_porte} porte`         : null,
      ].filter(Boolean).join(' · ') || 'nessun impianto censito';

      const manut = [c.mese_manutenzione_1, c.mese_manutenzione_2].filter(Boolean).join(' / ');
      const prossimo = c.prossimo_intervento
        ? new Date(c.prossimo_intervento).toLocaleDateString('it-IT') : null;

      mk.bindPopup(`
        <div style="font-size:13px;min-width:210px">
          <div style="font-weight:700;margin-bottom:4px">${c.nome}</div>
          <div style="color:#6b7280;font-size:12px">${c.indirizzo || ''}</div>
          <div style="color:#6b7280;font-size:12px;margin-bottom:6px">${c.cap || ''} ${c.citta || ''}</div>
          <div style="font-size:11px;margin-bottom:2px">
            <span style="background:${col};color:#fff;padding:1px 7px;border-radius:9px">${c.zona || 'senza zona'}</span>
            ${c.sospeso ? '<span style="background:#e0f2fe;color:#0369a1;padding:1px 7px;border-radius:9px;margin-left:4px">congelato</span>' : ''}
          </div>
          <div style="font-size:12px;margin-top:6px">🧯 ${impianti}</div>
          ${manut ? `<div style="font-size:12px">🗓️ manutenzioni: ${manut}</div>` : ''}
          ${prossimo ? `<div style="font-size:12px">📌 prossimo intervento: ${prossimo}</div>` : ''}
          ${c.telefono ? `<div style="font-size:12px">📞 ${c.telefono}</div>` : ''}
          ${c.geo_stato === 'approssimato' ? '<div style="font-size:11px;color:#d97706;margin-top:4px">⚠️ posizione approssimata</div>' : ''}
          <button id="vai-${c.id}" style="margin-top:8px;width:100%;padding:5px;
            background:#b30000;color:#fff;border:none;border-radius:5px;cursor:pointer;font-size:12px">
            Apri scheda →</button>
        </div>`);

      mk.on('popupopen', () => {
        const b = document.getElementById(`vai-${c.id}`);
        if (b) b.onclick = () => navigate(`/clienti/${c.id}`);
      });

      markerRef.current.push(mk);
    });

    if (visibili.length > 0) {
      const gruppo = L.featureGroup(markerRef.current);
      m.fitBounds(gruppo.getBounds().pad(0.15));
    }
  }, [visibili, navigate]);

  const alternaZona = z => {
    setZone(prec => {
      const set = new Set(prec || zone);
      if (set.has(z)) set.delete(z); else set.add(z);
      return set.size === zone.length ? null : set;
    });
  };

  const contaPerZona = z => clienti.filter(c => c.zona === z).length;

  return (
    <div className="page-content">
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12, flexWrap:'wrap' }}>
        <h2 style={{ margin:0, fontSize:18 }}>🗺️ Mappa clienti</h2>
        <span style={{ fontSize:13, color:'#6b7280' }}>
          {visibili.length} di {clienti.length} visualizzati
        </span>
        <input className="form-control" placeholder="🔍 Cerca cliente o via..."
          value={cerca} onChange={e => setCerca(e.target.value)}
          style={{ maxWidth:260, marginLeft:'auto' }} />
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
          <input type="checkbox" checked={includiSospesi}
            onChange={e => setSospesi(e.target.checked)} />
          Mostra congelati
        </label>
      </div>

      {senzaCoord > 0 && (
        <div style={{ padding:'8px 12px', background:'#fef9c3', color:'#854d0e',
                      borderRadius:8, fontSize:13, marginBottom:12 }}>
          {senzaCoord} clienti non compaiono sulla mappa perché l'indirizzo non è stato geolocalizzato.
        </div>
      )}

      {/* Legenda / filtro per zona */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
        {zone.map(z => {
          const attiva = !zoneAttive || zoneAttive.has(z);
          return (
            <button key={z} onClick={() => alternaZona(z)}
              style={{
                display:'flex', alignItems:'center', gap:6, cursor:'pointer',
                border:`1px solid ${attiva ? coloreZona(z) : '#e5e7eb'}`,
                background: attiva ? coloreZona(z) + '18' : '#fff',
                color: attiva ? '#1f2937' : '#9ca3af',
                borderRadius:14, padding:'3px 10px', fontSize:12,
              }}>
              <span style={{ width:10, height:10, borderRadius:'50%',
                             background: attiva ? coloreZona(z) : '#d1d5db' }} />
              {z} <span style={{ opacity:.6 }}>{contaPerZona(z)}</span>
            </button>
          );
        })}
        {zoneAttive && (
          <button onClick={() => setZone(null)}
            style={{ border:'none', background:'#f1f5f9', borderRadius:14,
                     padding:'3px 12px', fontSize:12, cursor:'pointer' }}>
            ✕ mostra tutte
          </button>
        )}
      </div>

      {loading && <div style={{ fontSize:13, color:'#6b7280', marginBottom:8 }}>Caricamento…</div>}

      <div ref={divRef}
        style={{ width:'100%', height:'calc(100vh - 300px)', minHeight:420,
                 borderRadius:10, border:'1px solid #e5e7eb' }} />
    </div>
  );
}
